/**
 * WlTenantDetailsModal.tsx
 * ----------------------------------------------------------------------------
 * Modal de detalhes do Tenant para o painel White Label, aberto ao clicar
 * em uma linha da listagem /wl/tenants.
 *
 * 100% server-side: consome as RPCs `wl_get_tenant_*` / `wl_list_tenant_*`
 * via hooks em `@/hooks/wl/useWlTenantDetails`. NUNCA faz SELECT direto.
 *
 * Layout:
 *   - Dialog fullscreen-mobile / max-w-[95vw] h-[92dvh] desktop
 *   - Tabs: Visão Geral · Faturamento · Usuários · Inboxes · Faturas
 *   - KPIs no padrão MyActivitiesKpiCard (anel SVG + ícone tonal)
 *   - Gráfico via MyActivitiesDailyBarChart (recharts)
 *
 * RELEASE: 2026-05-29 v1.0.0
 */
import { useMemo } from "react";
import { format, formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
// ScrollArea removido: substituído por container nativo overflow-y-auto para
// evitar que o viewport do Radix ScrollArea estique a largura junto com tabelas largas.
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Users,
  Inbox,
  MessageSquare,
  Layers,
  Calendar,
  TrendingUp,
  CheckSquare,
  Briefcase,
  DollarSign,
  Clock,
  ExternalLink,
  Wifi,
  WifiOff,
  Power,
  AlertCircle,
  Loader2,
  Instagram,
  MessagesSquare,
  Send,
  Globe,
  UserCheck,
  UserX,
  Crown,
  Activity,
  Sparkles,
  Zap,
  Package,
  ShieldAlert,
  PowerOff,
  MoreHorizontal,
  Gift,
} from "lucide-react";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useWlPermissions } from "@/contexts/WlPermissionsContext";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";
import { Button } from "@/components/ui/button";
import { StartSupportSessionDialog } from "@/components/wl/StartSupportSessionDialog";
import { WlTenantSessionsTab } from "@/components/wl/WlTenantSessionsTab";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
  YAxis,
  Legend,
} from "recharts";
import { useToast } from "@/hooks/use-toast";
import { describeWlError } from "@/hooks/wl/useWlErrorToast";
import { MyActivitiesKpiCard } from "@/components/activities/dashboard/premium/MyActivitiesKpiCard";
import {
  useWlTenantOverview,
  useWlTenantTimeseries,
  useWlTenantInboxes,
  useWlTenantMembers,
  useWlTenantBillingSummary,
  useWlTenantBillingHistory,
  useWlTenantAddons,
} from "@/hooks/wl/useWlTenantDetails";
import { cn } from "@/lib/utils";
import { WlTenantPlanBindingCard } from "@/components/wl/WlTenantPlanBindingCard";
import { WlTenantAddonsCard } from "@/components/wl/WlTenantAddonsCard";
import { WlTenantMasterCustomerCard } from "@/components/wl/WlTenantMasterCustomerCard";
import { WlTenantCourtesyButton } from "@/components/wl/WlTenantCourtesyButton";
import { WlTransferOwnerDialog } from "@/components/wl/WlTransferOwnerDialog";
import { useWlTenantCourtesy } from "@/hooks/wl/useWlTenantCourtesy";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// ---------------------------------------------------------------------------
// Helpers de formatação
// ---------------------------------------------------------------------------
const fmtDate = (iso: string | null | undefined) =>
  iso ? format(new Date(iso), "dd/MM/yyyy", { locale: ptBR }) : "—";

const fmtDateTime = (iso: string | null | undefined) =>
  iso ? format(new Date(iso), "dd/MM/yyyy HH:mm", { locale: ptBR }) : "—";

const fmtMoney = (n: number | null | undefined, currency = "BRL") => {
  if (n === null || n === undefined) return "—";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: (currency || "BRL").toUpperCase(),
  }).format(n);
};

const STATUS_TONE: Record<string, string> = {
  active: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
  trial: "bg-amber-500/10 text-amber-700 border-amber-500/30",
  suspended: "bg-rose-500/10 text-rose-700 border-rose-500/30",
  cancelled: "bg-zinc-500/10 text-zinc-700 border-zinc-500/30",
};

const STATUS_LABEL: Record<string, string> = {
  active: "Ativo",
  trial: "Trial",
  suspended: "Suspenso",
  cancelled: "Cancelado",
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface Props {
  tenantId: string | null;
  open: boolean;
  onClose: () => void;
}

// ===========================================================================
// Componente principal
// ===========================================================================
export function WlTenantDetailsModal({ tenantId, open, onClose }: Props) {
  const { toast } = useToast();
  const [startSupportOpen, setStartSupportOpen] = useState(false);
  // Menu "Ações": transferência de proprietário + disparo da cortesia
  const [transferOpen, setTransferOpen] = useState(false);
  const [courtesySignal, setCourtesySignal] = useState(0);
  const courtesyQ = useWlTenantCourtesy(tenantId);
  const { canDelete, canEdit, roleSlug } = useWlPermissions();
  // Transferir proprietário: somente administradores do painel WL (banco revalida)
  const canTransferOwner = canEdit("wl.tenants") && (roleSlug === "owner_wl" || roleSlug === "admin_wl");
  const { confirmDialog, ConfirmDialogComponent } = useConfirmDialog();
  const qc = useQueryClient();

  // ---------------------------------------------------------------------
  // Mutation: suspensão manual do tenant (reutiliza tenant_billing_apply_enforcement)
  // Exige permissão delete em wl.tenants; visível apenas para status ativos.
  // ---------------------------------------------------------------------
  const suspendMutation = useMutation({
    mutationFn: async (reason: string | null) => {
      if (!tenantId) throw new Error("Tenant inválido");
      const { data, error } = await supabase.rpc("wl_suspend_tenant_manual", {
        p_tenant_id: tenantId,
        p_reason: reason,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: "Tenant suspenso",
        description: "Inboxes e instâncias foram desativadas.",
      });
      // Invalida caches do modal e da listagem
      qc.invalidateQueries({ queryKey: ["wl_tenant_overview", tenantId] });
      qc.invalidateQueries({ queryKey: ["wl_tenant_inboxes", tenantId] });
      qc.invalidateQueries({ queryKey: ["wl_tenant_billing_summary", tenantId] });
      qc.invalidateQueries({ queryKey: ["wl-tenants-list"] });
    },
    onError: (err) => {
      toast({
        title: "Falha ao suspender",
        description: describeWlError(err),
        variant: "destructive",
      });
    },
  });

  const handleSuspend = async () => {
    const ok = await confirmDialog({
      title: "Suspender tenant manualmente?",
      description:
        "Esta ação executa o mesmo procedimento do cron automático: o tenant fica suspenso, todas as inboxes ficam inativas e as instâncias de provedor são desativadas. O acesso do cliente será bloqueado imediatamente.",
      warningMessage:
        "Só use este botão para tenants em estruturas de plano antigas ou situações excepcionais.",
      variant: "danger",
      confirmText: "Suspender agora",
      cancelText: "Cancelar",
    });
    if (ok) suspendMutation.mutate(null);
  };




  // Carga em paralelo (todas habilitadas só com tenantId)
  const overviewQ = useWlTenantOverview(tenantId);
  const timeseriesQ = useWlTenantTimeseries(tenantId, 7);
  const inboxesQ = useWlTenantInboxes(tenantId);
  const membersQ = useWlTenantMembers(tenantId);
  const billingQ = useWlTenantBillingSummary(tenantId);
  const historyQ = useWlTenantBillingHistory(tenantId, { limit: 50 });
  const addonsQ = useWlTenantAddons(tenantId);

  // Toast unificado para qualquer erro de RPC
  const firstError =
    overviewQ.error ||
    timeseriesQ.error ||
    inboxesQ.error ||
    membersQ.error ||
    billingQ.error ||
    historyQ.error;
  if (firstError && open) {
    // Toast só ao abrir (evita loops)
    setTimeout(() => {
      toast({
        title: "Erro ao carregar detalhes",
        description: describeWlError(firstError),
        variant: "destructive",
      });
    }, 0);
  }

  const overview = overviewQ.data;

  // Série pronta para o BarChart (mensagens in + out)
  const chartData = useMemo(
    () =>
      (timeseriesQ.data ?? []).map((p) => ({
        day: p.day,
        Entrada: p.messages_in,
        Saída: p.messages_out,
        Conversas: p.conversations,
      })),
    [timeseriesQ.data],
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className={cn(
          // Mobile: fullscreen | Desktop: 95vw × 92dvh
          // flex-col sobrescreve o `grid` padrão do DialogContent para que
          // header + tabs + área rolável se comportem como column real
          "p-0 gap-0 overflow-hidden flex flex-col",
          "max-w-[100vw] h-[100dvh] sm:max-w-[95vw] sm:h-[92dvh]",
          "sm:rounded-xl",
        )}
      >
        {/* ============================= HEADER ============================= */}
        <DialogHeader className="px-4 sm:px-6 py-3 border-b shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <DialogTitle className="flex items-center gap-2 text-base sm:text-lg truncate">
                {overview?.name ?? "Carregando…"}
                {overview?.status && (
                  <Badge
                    variant="outline"
                    className={cn(
                      "h-5 text-[10px] font-medium",
                      STATUS_TONE[overview.status] || "",
                    )}
                  >
                    {STATUS_LABEL[overview.status] || overview.status}
                  </Badge>
                )}
                {/* Badge de cortesia — aparece somente quando is_courtesy=true */}
                <WlTenantCourtesyButton tenantId={tenantId} badgeOnly />
              </DialogTitle>
              <p className="text-xs text-muted-foreground mt-0.5 truncate">
                <span className="font-mono">{overview?.slug ?? "…"}</span>
                {overview?.plan_name && (
                  <>
                    {" · "}
                    Plano <span className="font-medium text-foreground">{overview.plan_name}</span>
                  </>
                )}
                {overview?.created_at && (
                  <>
                    {" · "}
                    Criado em {fmtDate(overview.created_at)}
                  </>
                )}
              </p>
            </div>
            {/* Ações do header: Suspender manual + Acessar como Suporte */}
            <div className="flex items-center gap-2 shrink-0 mr-8">
              {/* Suspensão manual — só aparece p/ operadores com permissão delete
                  em wl.tenants e quando o tenant está em status ativo/trial/past_due */}
              {tenantId &&
                canDelete("wl.tenants") &&
                overview?.status &&
                ["active", "trial", "past_due"].includes(overview.status) && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1.5 border-rose-500/40 text-rose-700 hover:bg-rose-500/10 hover:text-rose-800 dark:text-rose-300"
                    onClick={handleSuspend}
                    disabled={suspendMutation.isPending}
                  >
                    {suspendMutation.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <PowerOff className="h-3.5 w-3.5" />
                    )}
                    Suspender
                  </Button>
                )}
              {tenantId && (
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5 border-amber-500/40 text-amber-700 hover:bg-amber-500/10 hover:text-amber-800 dark:text-amber-300"
                  onClick={() => setStartSupportOpen(true)}
                >
                  <ShieldAlert className="h-3.5 w-3.5" />
                  Acessar como Suporte
                </Button>
              )}
              {/* Menu Ações: agrupa ações menos frequentes */}
              {tenantId && (canEdit("wl.tenants") || canTransferOwner) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="outline" className="gap-1.5">
                      <MoreHorizontal className="h-3.5 w-3.5" />
                      Ações
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {canEdit("wl.tenants") && (
                      <DropdownMenuItem onSelect={() => setCourtesySignal((n) => n + 1)}>
                        <Gift className="mr-2 h-3.5 w-3.5" />
                        {courtesyQ.data?.is_courtesy ? "Remover cortesia" : "Marcar cortesia"}
                      </DropdownMenuItem>
                    )}
                    {canTransferOwner && (
                      <DropdownMenuItem onSelect={() => setTransferOpen(true)}>
                        <Crown className="mr-2 h-3.5 w-3.5" />
                        Transferir proprietário
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {/* Dialogs da cortesia (acionados pelo menu) */}
              <WlTenantCourtesyButton tenantId={tenantId} menuMode openSignal={courtesySignal} />
            </div>

          </div>
        </DialogHeader>


        {/* ============================ CONTEÚDO ============================ */}
        <Tabs defaultValue="overview" className="flex-1 flex flex-col min-h-0">
          <TabsList className="mx-4 sm:mx-6 mt-3 self-start shrink-0">
            <TabsTrigger value="overview">Visão Geral</TabsTrigger>
            <TabsTrigger value="billing">Faturamento</TabsTrigger>
            <TabsTrigger value="users">
              Usuários{overview ? ` (${overview.members_count})` : ""}
            </TabsTrigger>
            <TabsTrigger value="inboxes">
              Inboxes{overview ? ` (${overview.inboxes_count})` : ""}
            </TabsTrigger>
            <TabsTrigger value="invoices">Faturas</TabsTrigger>
            <TabsTrigger value="sessions">Sessões</TabsTrigger>
          </TabsList>

          {/* Container nativo: rolagem vertical real + bloqueio horizontal no nível do modal */}
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
            <div className="px-4 sm:px-6 pb-6 pt-4 min-w-0 w-full">
              {/* ====================== VISÃO GERAL ====================== */}
              <TabsContent value="overview" className="mt-0 space-y-4">
                <OverviewTab
                  overview={overview ?? null}
                  loading={overviewQ.isLoading}
                  chartData={chartData}
                  chartLoading={timeseriesQ.isLoading}
                  addons={addonsQ.data ?? []}
                />

              </TabsContent>

              {/* ====================== FATURAMENTO ====================== */}
              <TabsContent value="billing" className="mt-0">
                <BillingTab
                  summary={billingQ.data ?? null}
                  loading={billingQ.isLoading}
                  tenantId={tenantId}
                />

              </TabsContent>

              {/* ======================= USUÁRIOS ======================= */}
              <TabsContent value="users" className="mt-0">
                <MembersTab
                  members={membersQ.data ?? []}
                  loading={membersQ.isLoading}
                  maxAgents={overview?.max_agents ?? null}
                />
              </TabsContent>

              {/* ======================= INBOXES ======================== */}
              <TabsContent value="inboxes" className="mt-0">
                <InboxesTab
                  inboxes={inboxesQ.data ?? []}
                  loading={inboxesQ.isLoading}
                  maxChannels={overview?.max_channels ?? null}
                />
              </TabsContent>

              {/* ======================== FATURAS ======================== */}
              <TabsContent value="invoices" className="mt-0">
                <InvoicesTab
                  invoices={historyQ.data ?? []}
                  loading={historyQ.isLoading}
                />
              </TabsContent>

              {/* ======================== SESSÕES ======================== */}
              <TabsContent value="sessions" className="mt-0">
                <WlTenantSessionsTab tenantId={tenantId} />
              </TabsContent>
            </div>
          </div>
        </Tabs>
      </DialogContent>

      {/* Modal de iniciar sessão de suporte */}
      <WlTransferOwnerDialog tenantId={tenantId} open={transferOpen} onOpenChange={setTransferOpen} />
      <StartSupportSessionDialog
        open={startSupportOpen}
        tenantId={tenantId}
        tenantName={overview?.name ?? null}
        onClose={() => setStartSupportOpen(false)}
      />

      {/* Diálogo de confirmação da suspensão manual */}
      {ConfirmDialogComponent}
    </Dialog>
  );
}

// ===========================================================================
// Tabs internas
// ===========================================================================

// ---------------------------------------------------------------------------
// 1) Visão Geral — KPIs grandes + Gráfico + Uso de Recursos + Add-ons + Owner
//    Layout espelhado da Visão Geral do painel admin do tenant
//    (src/components/tenant/management/OverviewTab.tsx).
// ---------------------------------------------------------------------------

// Tipos auxiliares
type OverviewData = ReturnType<typeof useWlTenantOverview>["data"];
type AddonItem = {
  id: string;
  addon_name: string;
  quantity: number;
  addon_type: string;
};

// Mapeia o id/slug do plano para um ícone temático
const getPlanIcon = (planId: string | null | undefined) => {
  switch ((planId || "").toLowerCase()) {
    case "enterprise":
      return Crown;
    case "professional":
      return Sparkles;
    case "starter":
      return Zap;
    default:
      return Package;
  }
};

// ===========================================================================
// KpiCard — espelha o componente do painel admin (sem dependência externa)
// ===========================================================================
type KpiVariant = "primary" | "success" | "warning" | "info" | "default";

function KpiCard({
  title,
  value,
  description,
  icon: Icon,
  variant = "default",
  badge,
}: {
  title: string;
  value: React.ReactNode;
  description?: React.ReactNode;
  icon: React.ElementType;
  variant?: KpiVariant;
  badge?: { label: string; variant: "success" | "warning" | "destructive" };
}) {
  const styles: Record<KpiVariant, { iconBg: string; iconColor: string }> = {
    primary: { iconBg: "bg-primary/10", iconColor: "text-primary" },
    success: {
      iconBg: "bg-emerald-500/10",
      iconColor: "text-emerald-600 dark:text-emerald-400",
    },
    warning: {
      iconBg: "bg-amber-500/10",
      iconColor: "text-amber-600 dark:text-amber-400",
    },
    info: {
      iconBg: "bg-blue-500/10",
      iconColor: "text-blue-600 dark:text-blue-400",
    },
    default: { iconBg: "bg-muted", iconColor: "text-muted-foreground" },
  };
  const s = styles[variant];
  return (
    <Card className="relative overflow-hidden transition-all duration-300 hover:shadow-lg hover:translate-y-[-2px]">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      <CardContent className="p-6">
        <div className="flex items-center gap-4">
          <div
            className={cn(
              "flex h-12 w-12 items-center justify-center rounded-xl flex-shrink-0",
              s.iconBg,
            )}
          >
            <Icon className={cn("h-6 w-6", s.iconColor)} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-muted-foreground truncate">
                {title}
              </p>
              {badge && (
                <Badge
                  variant="secondary"
                  className={cn(
                    "text-xs px-2 py-0 h-5 border-0",
                    badge.variant === "success" &&
                      "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
                    badge.variant === "warning" &&
                      "bg-amber-500/10 text-amber-600 dark:text-amber-400",
                    badge.variant === "destructive" &&
                      "bg-rose-500/10 text-rose-600 dark:text-rose-400",
                  )}
                >
                  {badge.label}
                </Badge>
              )}
            </div>
            <p className="text-2xl font-bold tracking-tight mt-1 truncate">
              {value}
            </p>
            {description && (
              <p className="text-xs text-muted-foreground mt-1 truncate">
                {description}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ===========================================================================
// ResourceUsageBar — barra de uso vs limite (com estado "ilimitado")
// ===========================================================================
function ResourceUsageBar({
  label,
  current,
  max,
  icon: Icon,
  isUnlimited = false,
}: {
  label: string;
  current: number;
  max: number;
  icon: React.ElementType;
  isUnlimited?: boolean;
}) {
  const percentage = isUnlimited
    ? 0
    : max > 0
      ? Math.min((current / max) * 100, 100)
      : 0;
  const isNearLimit = percentage >= 80;
  const isAtLimit = percentage >= 100;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{label}</span>
        </div>
        <span
          className={cn(
            "font-mono",
            isAtLimit && "text-destructive",
            isNearLimit && !isAtLimit && "text-amber-600 dark:text-amber-400",
          )}
        >
          {current} / {isUnlimited ? "∞" : max}
        </span>
      </div>
      {!isUnlimited ? (
        <Progress
          value={percentage}
          className={cn(
            "h-2",
            isAtLimit && "[&>div]:bg-destructive",
            isNearLimit && !isAtLimit && "[&>div]:bg-amber-500",
          )}
        />
      ) : (
        <div className="h-2 rounded-full bg-muted relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-primary/40 to-primary/20 animate-pulse" />
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// OverviewTab — corpo da aba "Visão Geral"
// ===========================================================================
function OverviewTab({
  overview,
  loading,
  chartData,
  chartLoading,
  addons,
}: {
  overview: OverviewData | null;
  loading: boolean;
  chartData: Array<{
    day: string;
    Entrada: number;
    Saída: number;
    Conversas: number;
  }>;
  chartLoading: boolean;
  addons: AddonItem[];
}) {
  if (loading || !overview) {
    return <p className="text-sm text-muted-foreground">Carregando dados…</p>;
  }

  // -------------------------------------------------------------------------
  // Totais derivados
  // -------------------------------------------------------------------------
  // Soma do período 7d do gráfico (Entrada+Saída) — alinhado com o admin
  const periodTotals = chartData.reduce(
    (acc, d) => ({
      sent: acc.sent + (d.Saída || 0),
      received: acc.received + (d.Entrada || 0),
    }),
    { sent: 0, received: 0 },
  );
  const periodTotal = periodTotals.sent + periodTotals.received;

  // Limites efetivos (somam add-ons via wl_get_tenant_overview v1.1.0)
  const effMaxAgents = overview.effective_max_agents ?? overview.max_agents ?? 0;
  const effMaxChannels =
    overview.effective_max_channels ?? overview.max_channels ?? 0;
  const effMaxMessages = overview.effective_max_messages ?? 0;
  const messagesUnlimited = effMaxMessages <= 0;

  const agentsAvailable = Math.max(0, effMaxAgents - overview.agents_count);
  const inboxesAvailable = Math.max(0, effMaxChannels - overview.inboxes_count);

  // Status badge do plano
  const planBadge: { label: string; variant: "success" | "warning" | "destructive" } | undefined =
    overview.subscription_status === "active"
      ? { label: "Ativo", variant: "success" }
      : overview.subscription_status === "trialing"
        ? { label: "Trial", variant: "warning" }
        : overview.subscription_status === "past_due"
          ? { label: "Pgto pendente", variant: "destructive" }
          : overview.suspension_reason
            ? { label: "Suspenso", variant: "destructive" }
            : undefined;

  const nextBillingLabel = overview.current_period_end
    ? format(new Date(overview.current_period_end), "dd 'de' MMM", {
        locale: ptBR,
      })
    : "—";

  return (
    <div className="space-y-6">
      {/* ============= Seção 1: 4 KPIs principais (estilo admin) ============= */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          title="Plano Atual"
          value={overview.plan_name || overview.plan || "—"}
          description={`Próx. cobrança: ${nextBillingLabel}`}
          icon={getPlanIcon(overview.plan)}
          variant="primary"
          badge={planBadge}
        />
        <KpiCard
          title="Mensagens (7 dias)"
          value={periodTotal.toLocaleString("pt-BR")}
          description={`↑ ${periodTotals.sent.toLocaleString("pt-BR")} env. | ↓ ${periodTotals.received.toLocaleString("pt-BR")} rec.`}
          icon={MessageSquare}
          variant="success"
        />
        <KpiCard
          title="Agentes"
          value={`${overview.agents_count} / ${effMaxAgents}`}
          description={
            overview.addon_agents > 0
              ? `${agentsAvailable} disponíveis · +${overview.addon_agents} add-on`
              : `${agentsAvailable} disponíveis`
          }
          icon={Users}
          variant="info"
        />
        <KpiCard
          title="Inboxes"
          value={`${overview.inboxes_count} / ${effMaxChannels}`}
          description={
            overview.addon_channels > 0
              ? `${inboxesAvailable} disponíveis · +${overview.addon_channels} add-on`
              : `${inboxesAvailable} disponíveis`
          }
          icon={Inbox}
          variant="warning"
        />
      </div>

      {/* ============= Seção 2: Gráfico (7d) + Uso de Recursos ============= */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfico de Mensagens 7 dias */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base font-semibold">
                  Mensagens (7 dias)
                </CardTitle>
                <CardDescription>
                  Volume de mensagens enviadas e recebidas
                </CardDescription>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold">
                  {periodTotal.toLocaleString("pt-BR")}
                </p>
                <p className="text-xs text-muted-foreground">
                  ↑ {periodTotals.sent.toLocaleString("pt-BR")} | ↓{" "}
                  {periodTotals.received.toLocaleString("pt-BR")}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {chartLoading ? (
              <p className="text-sm text-muted-foreground py-8 text-center">
                Carregando…
              </p>
            ) : chartData.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">
                Sem atividade no período.
              </p>
            ) : (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    margin={{ top: 8, right: 8, left: -12, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      className="stroke-muted"
                    />
                    <XAxis
                      dataKey="day"
                      tickFormatter={(v) => {
                        const d = new Date(v);
                        return Number.isNaN(d.getTime())
                          ? v
                          : format(d, "dd/MM");
                      }}
                      tick={{
                        fontSize: 11,
                        fill: "hsl(var(--muted-foreground))",
                      }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{
                        fontSize: 11,
                        fill: "hsl(var(--muted-foreground))",
                      }}
                      axisLine={false}
                      tickLine={false}
                      width={32}
                    />
                    <ReTooltip
                      contentStyle={{
                        background: "hsl(var(--popover))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                      labelFormatter={(v) => {
                        const d = new Date(String(v));
                        return Number.isNaN(d.getTime())
                          ? String(v)
                          : format(d, "dd/MM/yyyy");
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar
                      dataKey="Entrada"
                      name="Recebidas"
                      fill="hsl(160 84% 45%)"
                      radius={[4, 4, 0, 0]}
                      maxBarSize={24}
                    />
                    <Bar
                      dataKey="Saída"
                      name="Enviadas"
                      fill="hsl(217 91% 60%)"
                      radius={[4, 4, 0, 0]}
                      maxBarSize={24}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Uso de Recursos */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">
              Uso de Recursos
            </CardTitle>
            <CardDescription>Consumo atual vs limites do plano</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <ResourceUsageBar
              label="Agentes"
              current={overview.agents_count}
              max={effMaxAgents}
              icon={Users}
            />
            <ResourceUsageBar
              label="Inboxes"
              current={overview.inboxes_count}
              max={effMaxChannels}
              icon={Inbox}
            />
            <ResourceUsageBar
              label="Mensagens/mês"
              current={overview.messages_month_current}
              max={messagesUnlimited ? 0 : effMaxMessages}
              icon={MessageSquare}
              isUnlimited={messagesUnlimited}
            />
          </CardContent>
        </Card>
      </div>

      {/* ============= Seção 3: Add-ons Ativos (chips) ============= */}
      {addons.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">
              Add-ons Ativos
            </CardTitle>
            <CardDescription>Recursos adicionais contratados</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {addons.map((addon) => (
                <Badge
                  key={addon.id}
                  variant="secondary"
                  className="px-3 py-1.5 text-sm"
                >
                  {addon.addon_name}
                  {addon.quantity > 1 && (
                    <span className="ml-1.5 text-xs text-muted-foreground">
                      x{addon.quantity}
                    </span>
                  )}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ============= Seção 4: Atividade dos últimos 7 dias (3 séries) ============= */}
      <Card className="rounded-xl border border-border/50 shadow-lg">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            Atividade dos últimos 7 dias
          </CardTitle>
          <CardDescription>
            Conversas, mensagens recebidas e enviadas
          </CardDescription>
        </CardHeader>
        <CardContent>
          {chartLoading ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              Carregando…
            </p>
          ) : chartData.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              Sem atividade no período.
            </p>
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{ top: 8, right: 8, left: -12, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    className="stroke-muted"
                  />
                  <XAxis
                    dataKey="day"
                    tickFormatter={(v) => {
                      const d = new Date(v);
                      return Number.isNaN(d.getTime()) ? v : format(d, "dd/MM");
                    }}
                    tick={{
                      fontSize: 11,
                      fill: "hsl(var(--muted-foreground))",
                    }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{
                      fontSize: 11,
                      fill: "hsl(var(--muted-foreground))",
                    }}
                    axisLine={false}
                    tickLine={false}
                    width={32}
                  />
                  <ReTooltip
                    contentStyle={{
                      background: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    labelFormatter={(v) => {
                      const d = new Date(String(v));
                      return Number.isNaN(d.getTime())
                        ? String(v)
                        : format(d, "dd/MM/yyyy");
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar
                    dataKey="Entrada"
                    fill="hsl(160 84% 45%)"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={24}
                  />
                  <Bar
                    dataKey="Saída"
                    fill="hsl(217 91% 60%)"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={24}
                  />
                  <Bar
                    dataKey="Conversas"
                    fill="hsl(43 96% 56%)"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={24}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ============= Seção 5: Owner do tenant (compacta) ============= */}
      <Card className="rounded-xl border border-border/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Users className="h-4 w-4" /> Owner
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <div>
            <div className="text-xs text-muted-foreground">Nome</div>
            <div className="font-medium truncate">
              {overview.owner_name ?? "—"}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Email</div>
            <div className="font-mono text-xs truncate">
              {overview.owner_email_masked ?? "—"}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Telefone</div>
            <div className="font-mono text-xs truncate">
              {overview.owner_phone_masked ?? "—"}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


// ---------------------------------------------------------------------------
// 2) Faturamento — resumo da assinatura
// ---------------------------------------------------------------------------
function BillingTab({
  summary,
  loading,
  tenantId,
}: {
  summary: ReturnType<typeof useWlTenantBillingSummary>["data"] | null;
  loading: boolean;
  tenantId: string | null;
}) {
  if (loading || !summary) {
    return <p className="text-sm text-muted-foreground">Carregando faturamento…</p>;
  }


  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MyActivitiesKpiCard
          icon={DollarSign}
          tone="teal"
          label="MRR estimado"
          value={fmtMoney(summary.mrr)}
          hint="Plano mensal + add-ons ativos"
        />
        <MyActivitiesKpiCard
          icon={Layers}
          tone="violet"
          label="Add-ons ativos"
          value={summary.active_addons_count.toLocaleString("pt-BR")}
          hint={`Total mensal: ${fmtMoney(summary.addons_total_monthly)}`}
        />
        {/* KPI 3 — Em trial mostra "Trial restante"; senão "Em Aberto" (valor + qtd) */}
        {summary.is_in_trial ? (
          <MyActivitiesKpiCard
            icon={Clock}
            tone="amber"
            label="Trial restante"
            value={`${summary.trial_days_remaining}d`}
            hint={`Termina em ${fmtDate(summary.trial_ends_at)}`}
          />
        ) : (
          <MyActivitiesKpiCard
            icon={Clock}
            tone="sky"
            label="Em Aberto"
            value={fmtMoney(summary.open_invoices_total ?? 0)}
            hint={
              (summary.open_invoices_count ?? 0) > 0
                ? `${summary.open_invoices_count} fatura(s) · próx. ${fmtDate(summary.next_due_date)}`
                : "Nenhuma fatura em aberto"
            }
          />
        )}
        {/* KPI 4 — Vencidas (substitui "Última fatura") */}
        <MyActivitiesKpiCard
          icon={AlertCircle}
          tone="rose"
          label="Vencidas"
          value={fmtMoney(summary.overdue_invoices_total ?? 0)}
          hint={
            (summary.overdue_invoices_count ?? 0) > 0
              ? `${summary.overdue_invoices_count} fatura(s) vencida(s)`
              : "Nenhuma fatura vencida"
          }
        />

      </div>

      {tenantId && <WlTenantMasterCustomerCard tenantId={tenantId} />}
      {tenantId && <WlTenantPlanBindingCard tenantId={tenantId} />}
      {tenantId && <WlTenantAddonsCard tenantId={tenantId} />}



      <Card className="rounded-xl border border-border/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Detalhes da assinatura</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs">
          <Row label="Plano" value={summary.plan_name ?? summary.plan_id ?? "—"} />
          <Row label="Status" value={summary.subscription_status ?? "—"} />
          <Row label="Periodicidade" value={summary.billing_period ?? "—"} />
          <Row label="Preço mensal" value={fmtMoney(summary.plan_price_monthly)} />
          <Row label="Preço anual" value={fmtMoney(summary.plan_price_yearly)} />
          <Row label="Setup pago" value={summary.setup_fee_paid ? "Sim" : "Não"} />
          <Row label="Início do período" value={fmtDate(summary.current_period_start)} />
          <Row label="Fim do período" value={fmtDate(summary.current_period_end)} />
          <Row label="Trial até" value={fmtDate(summary.trial_ends_at)} />
          <Row label="Cancelado em" value={fmtDate(summary.canceled_at)} />
        </CardContent>
      </Card>
    </div>
  );
}

// Linha label/valor compacta para cartões de resumo (usada por BillingTab)
function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground font-medium truncate text-right">{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 3) Membros do tenant
// ---------------------------------------------------------------------------
function MembersTab({
  members,
  loading,
  maxAgents,
}: {
  members: ReturnType<typeof useWlTenantMembers>["data"];
  loading: boolean;
  maxAgents: number | null;
}) {
  // KPIs calculados client-side a partir da lista de membros
  const kpis = useMemo(() => {
    const list = members ?? [];
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return {
      total: list.length,
      active: list.filter((m) => m.is_active).length,
      inactive: list.filter((m) => !m.is_active).length,
      owners: list.filter((m) => m.is_owner).length,
      online7d: list.filter(
        (m) => m.last_seen_at && new Date(m.last_seen_at).getTime() >= sevenDaysAgo,
      ).length,
    };
  }, [members]);

  if (loading) return <p className="text-sm text-muted-foreground">Carregando membros…</p>;

  return (
    <div className="space-y-3">
      {/* KPIs (mesmo padrão da aba Inboxes) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <InboxKpi
          label="Usuários"
          value={`${kpis.total} / ${maxAgents ?? "∞"}`}
          icon={Users}
          tone="primary"
        />
        <InboxKpi label="Ativos" value={kpis.active} icon={UserCheck} tone="success" />
        <InboxKpi label="Inativos" value={kpis.inactive} icon={UserX} tone="muted" />
        <InboxKpi label="Owners" value={kpis.owners} icon={Crown} tone="warning" />
        <InboxKpi label="Online (7d)" value={kpis.online7d} icon={Activity} tone="primary" />
      </div>

      {!members || members.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhum membro encontrado.</p>
      ) : (
        <Card className="rounded-xl border border-border/50 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Nome</TableHead>
                <TableHead className="text-xs">Email</TableHead>
                <TableHead className="text-xs">Papéis</TableHead>
                <TableHead className="text-xs">Status</TableHead>
                <TableHead className="text-xs">Criado em</TableHead>
                <TableHead className="text-xs">Última atividade</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((m) => (
                <TableRow key={m.member_id}>
                  <TableCell className="py-2 text-xs">
                    <div className="font-medium flex items-center gap-1.5">
                      {m.full_name ?? "—"}
                      {m.is_owner && (
                        <Badge variant="outline" className="h-4 text-[9px] px-1">
                          Owner
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="py-2 text-xs font-mono text-muted-foreground">
                    {m.email_masked ?? "—"}
                  </TableCell>
                  <TableCell className="py-2 text-xs">
                    {m.roles.length === 0 ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {m.roles.map((r, i) => (
                          <Badge key={i} variant="outline" className="h-4 text-[9px] px-1">
                            {r.role}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="py-2 text-xs">
                    <span
                      className={cn(
                        "inline-flex items-center gap-1.5",
                        m.is_active ? "text-emerald-600" : "text-zinc-500",
                      )}
                    >
                      <span
                        className={cn(
                          "h-2 w-2 rounded-full",
                          m.is_active ? "bg-emerald-500" : "bg-zinc-400",
                        )}
                      />
                      {m.is_active ? "Ativo" : "Inativo"}
                    </span>
                  </TableCell>
                  <TableCell className="py-2 text-xs text-muted-foreground whitespace-nowrap">
                    {fmtDateTime(m.joined_at)}
                  </TableCell>
                  <TableCell className="py-2 text-xs text-muted-foreground whitespace-nowrap">
                    {m.last_seen_at
                      ? formatDistanceToNow(new Date(m.last_seen_at), {
                          addSuffix: true,
                          locale: ptBR,
                        })
                      : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}

// Glyph SVG do WhatsApp (verde ou herdando cor)
const WhatsAppGlyph = ({ color }: { color: string }) => (
  <svg viewBox="0 0 32 32" className="h-4 w-4" aria-hidden="true">
    <path
      fill={color}
      d="M16 2.667C8.636 2.667 2.667 8.636 2.667 16c0 2.355.621 4.66 1.8 6.687L2.667 29.333l6.84-1.78A13.31 13.31 0 0 0 16 29.333C23.364 29.333 29.333 23.364 29.333 16S23.364 2.667 16 2.667Zm7.74 18.86c-.328.92-1.62 1.694-2.66 1.92-.71.15-1.638.27-4.762-1.024-3.996-1.654-6.57-5.71-6.77-5.974-.2-.264-1.6-2.124-1.6-4.05 0-1.928 1.012-2.872 1.37-3.26.296-.32.78-.464 1.246-.464.15 0 .286.008.41.014.358.016.538.036.774.602.296.71 1.006 2.464 1.094 2.644.09.18.18.426.054.69-.118.27-.224.382-.404.59-.18.21-.35.37-.53.6-.164.198-.348.41-.14.762.21.35.928 1.532 1.99 2.478 1.37 1.22 2.5 1.61 2.86 1.768.27.118.59.094.79-.118.252-.27.566-.722.886-1.166.226-.318.51-.358.812-.244.31.106 1.95.918 2.288 1.084.338.166.56.246.644.388.082.146.082.842-.244 1.62Z"
    />
  </svg>
);

/** Ícone do canal — mesmo padrão visual do ConversationList do chat. */
function ChannelIcon({
  channelType,
  providerSlug,
}: {
  channelType?: string | null;
  providerSlug?: string | null;
}) {
  if (!channelType) return <Inbox className="h-4 w-4 text-muted-foreground" />;
  const isWaba =
    channelType === "whatsapp_oficial" ||
    (channelType === "whatsapp" && !!providerSlug && /meta|waba|cloud/i.test(providerSlug));
  if (isWaba) {
    return (
      <span className="text-foreground inline-flex">
        <WhatsAppGlyph color="currentColor" />
      </span>
    );
  }
  if (channelType === "whatsapp") return <WhatsAppGlyph color="#25D366" />;
  if (channelType === "instagram") return <Instagram className="h-4 w-4 text-pink-500" />;
  if (channelType === "messenger") return <MessagesSquare className="h-4 w-4 text-blue-500" />;
  if (channelType === "telegram") return <Send className="h-4 w-4 text-sky-500" />;
  if (channelType === "webchat") return <Globe className="h-4 w-4 text-muted-foreground" />;
  return <Inbox className="h-4 w-4 text-muted-foreground" />;
}

/** Configuração visual por status de inbox (rótulo PT-BR + ícone Wifi/etc). */
type InboxStatusCfg = {
  label: string;
  icon: typeof Wifi;
  className: string;
  iconClassName: string;
};
const INBOX_STATUS_CONFIG: Record<string, InboxStatusCfg> = {
  active: {
    label: "Conectado",
    icon: Wifi,
    className: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
    iconClassName: "text-green-600 dark:text-green-400",
  },
  inactive: {
    label: "Inativo",
    icon: Power,
    className: "bg-muted text-muted-foreground",
    iconClassName: "text-muted-foreground",
  },
  connecting: {
    label: "Conectando...",
    icon: Loader2,
    className: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    iconClassName: "text-blue-600 dark:text-blue-400 animate-spin",
  },
  error: {
    label: "Erro",
    icon: AlertCircle,
    className: "bg-destructive/10 text-destructive",
    iconClassName: "text-destructive",
  },
  disconnected: {
    label: "Desconectado",
    icon: WifiOff,
    className: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
    iconClassName: "text-orange-600 dark:text-orange-400",
  },
  suspended: {
    label: "Suspenso",
    icon: Power,
    className: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    iconClassName: "text-amber-600 dark:text-amber-400",
  },
  pending: {
    label: "Pendente",
    icon: Clock,
    className: "bg-zinc-100 text-zinc-700 dark:bg-zinc-900/30 dark:text-zinc-400",
    iconClassName: "text-zinc-600 dark:text-zinc-400",
  },
  pending_setup: {
    label: "Aguardando setup",
    icon: Clock,
    className: "bg-zinc-100 text-zinc-700 dark:bg-zinc-900/30 dark:text-zinc-400",
    iconClassName: "text-zinc-600 dark:text-zinc-400",
  },
};

function InboxStatusBadge({ status }: { status: string }) {
  const cfg = INBOX_STATUS_CONFIG[status] ?? INBOX_STATUS_CONFIG.inactive;
  const Icon = cfg.icon;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium",
        cfg.className,
      )}
    >
      <Icon className={cn("h-3 w-3", cfg.iconClassName)} />
      {cfg.label}
    </span>
  );
}

/** Mini-card de KPI da aba Inboxes. */
function InboxKpi({
  label,
  value,
  icon: Icon,
  tone,
}: {
  label: string;
  value: string | number;
  icon: typeof Wifi;
  tone: "primary" | "success" | "warning" | "danger" | "muted";
}) {
  const toneMap = {
    primary: "bg-primary/10 text-primary",
    success: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
    warning: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
    danger: "bg-destructive/10 text-destructive",
    muted: "bg-muted text-muted-foreground",
  } as const;
  return (
    <Card className="rounded-xl border border-border/50">
      <CardContent className="p-3 flex items-center gap-3">
        <div className={cn("h-9 w-9 rounded-lg flex items-center justify-center shrink-0", toneMap[tone])}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground truncate">{label}</p>
          <p className="text-base font-semibold leading-tight truncate">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// 4) Inboxes do tenant
// ---------------------------------------------------------------------------
function InboxesTab({
  inboxes,
  loading,
  maxChannels,
}: {
  inboxes: ReturnType<typeof useWlTenantInboxes>["data"];
  loading: boolean;
  maxChannels: number | null;
}) {
  const kpis = useMemo(() => {
    const list = inboxes ?? [];
    const by = (s: string) => list.filter((i) => i.status === s).length;
    return {
      total: list.length,
      active: by("active"),
      disconnected: by("disconnected"),
      suspended: by("suspended"),
      error: by("error"),
    };
  }, [inboxes]);

  if (loading) return <p className="text-sm text-muted-foreground">Carregando inboxes…</p>;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <InboxKpi
          label="Inboxes"
          value={`${kpis.total} / ${maxChannels ?? "∞"}`}
          icon={Inbox}
          tone="primary"
        />
        <InboxKpi label="Conectadas" value={kpis.active} icon={Wifi} tone="success" />
        <InboxKpi label="Desconectadas" value={kpis.disconnected} icon={WifiOff} tone="warning" />
        <InboxKpi label="Suspensas" value={kpis.suspended} icon={Power} tone="warning" />
        <InboxKpi label="Com erro" value={kpis.error} icon={AlertCircle} tone="danger" />
      </div>

      {!inboxes || inboxes.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhuma inbox cadastrada.</p>
      ) : (
        <Card className="rounded-xl border border-border/50 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs w-10" />
                <TableHead className="text-xs">Nome</TableHead>
                <TableHead className="text-xs">Canal</TableHead>
                <TableHead className="text-xs">Provedor</TableHead>
                <TableHead className="text-xs">Status</TableHead>
                <TableHead className="text-xs text-center">Conv. 30d</TableHead>
                <TableHead className="text-xs">Última msg</TableHead>
                <TableHead className="text-xs">Criada em</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {inboxes.map((ix) => (
                <TableRow key={ix.inbox_id}>
                  <TableCell className="py-2 w-10">
                    <ChannelIcon
                      channelType={ix.channel_type}
                      providerSlug={ix.provider_name}
                    />
                  </TableCell>
                  <TableCell className="py-2 text-xs font-medium">
                    {ix.name}
                    {ix.is_default && (
                      <Badge variant="outline" className="ml-1.5 h-4 text-[9px] px-1">
                        default
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="py-2 text-xs text-muted-foreground">{ix.channel_type}</TableCell>
                  <TableCell className="py-2 text-xs">{ix.provider_name ?? "—"}</TableCell>
                  <TableCell className="py-2 text-xs">
                    <InboxStatusBadge status={ix.status} />
                  </TableCell>
                  <TableCell className="py-2 text-xs text-center">{ix.conversations_30d}</TableCell>
                  <TableCell className="py-2 text-xs text-muted-foreground whitespace-nowrap">
                    {fmtDateTime(ix.last_message_at)}
                  </TableCell>
                  <TableCell className="py-2 text-xs text-muted-foreground whitespace-nowrap">
                    {fmtDateTime(ix.created_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 5) Faturas (histórico)
// ---------------------------------------------------------------------------
// ===========================================================================
// InvoicesTab — Aba "Faturas" do modal do tenant (Painel WL)
// ---------------------------------------------------------------------------
// v1.1.2 — Novas colunas (Nº Fatura, Vencimento, Pago em, Meio Pagto,
//          Conta Corrente), periodicidade traduzida para PT-BR e ordenação
//          crescente por vencimento já vem da RPC wl_list_tenant_billing_history.
// ===========================================================================

// Tradução de billing_period (vindo da RPC em inglês) para rótulo PT-BR
const BILLING_PERIOD_LABEL_PTBR: Record<string, string> = {
  monthly: "Mensal",
  bimonthly: "Bimestral",
  quarterly: "Trimestral",
  semiannual: "Semestral",
  annual: "Anual",
};

function formatBillingPeriodPtBr(value: string | null | undefined): string | null {
  if (!value) return null;
  const key = value.toLowerCase();
  return BILLING_PERIOD_LABEL_PTBR[key] ?? value;
}

function InvoicesTab({
  invoices,
  loading,
}: {
  invoices: ReturnType<typeof useWlTenantBillingHistory>["data"];
  loading: boolean;
}) {
  if (loading) return <p className="text-sm text-muted-foreground">Carregando faturas…</p>;
  if (!invoices || invoices.length === 0) {
    return <p className="text-sm text-muted-foreground">Nenhuma fatura emitida.</p>;
  }

  return (
    <Card className="rounded-xl border border-border/50 overflow-hidden">
      {/* Wrapper com rolagem horizontal E vertical interna; cabeçalho fica fixo (sticky) */}
      <div className="w-full max-w-full max-h-[60vh] overflow-x-auto overflow-y-auto">
        <Table className="min-w-[1100px]">
          <TableHeader className="sticky top-0 z-10 bg-background">
            <TableRow>
              <TableHead className="text-xs">Nº Fatura</TableHead>
              <TableHead className="text-xs">Vencimento</TableHead>
              <TableHead className="text-xs">Plano</TableHead>
              <TableHead className="text-xs">Período</TableHead>
              <TableHead className="text-xs">Emitida em</TableHead>
              <TableHead className="text-xs">Pago em</TableHead>
              <TableHead className="text-xs">Meio Pagto</TableHead>
              <TableHead className="text-xs">Conta Corrente</TableHead>
              <TableHead className="text-xs">Valor</TableHead>
              <TableHead className="text-xs">Status</TableHead>
              <TableHead className="text-xs">Link</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.map((inv) => {
              const periodPtBr = formatBillingPeriodPtBr(inv.billing_period);
              return (
                <TableRow key={inv.id}>
                  {/* Nº Fatura (mesma referência exibida na listagem master de cobranças) */}
                  <TableCell className="py-2 text-xs font-mono">
                    <span
                      className="block max-w-[120px] truncate"
                      title={inv.numero_fatura ?? ""}
                    >
                      {inv.numero_fatura || "—"}
                    </span>
                  </TableCell>

                  {/* Vencimento (movido para logo após Nº Fatura) */}
                  <TableCell className="py-2 text-xs text-muted-foreground whitespace-nowrap">
                    {inv.due_date ? fmtDate(inv.due_date) : "—"}
                  </TableCell>

                  {/* Plano + periodicidade em PT-BR */}
                  <TableCell className="py-2 text-xs">
                    {inv.plan_name ?? "—"}
                    {periodPtBr && (
                      <span className="text-[10px] text-muted-foreground ml-1">
                        ({periodPtBr})
                      </span>
                    )}
                  </TableCell>

                  {/* Período (forward: vencimento → próximo vencimento) */}
                  <TableCell className="py-2 text-xs text-muted-foreground whitespace-nowrap">
                    {fmtDate(inv.period_start)} → {fmtDate(inv.period_end)}
                  </TableCell>

                  {/* Emitida em (movido para depois de Período) */}
                  <TableCell className="py-2 text-xs text-muted-foreground whitespace-nowrap">
                    {fmtDateTime(inv.issued_at)}
                  </TableCell>

                  {/* Pago em */}
                  <TableCell className="py-2 text-xs text-muted-foreground whitespace-nowrap">
                    {inv.payment_date ? fmtDate(inv.payment_date) : "—"}
                  </TableCell>

                  {/* Meio de Pagamento */}
                  <TableCell className="py-2 text-xs whitespace-nowrap">
                    {inv.payment_method_name || (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>

                  {/* Conta Corrente */}
                  <TableCell className="py-2 text-xs whitespace-nowrap">
                    {inv.bank_account_name || (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>

                  {/* Valor */}
                  <TableCell className="py-2 text-xs font-medium whitespace-nowrap">
                    {fmtMoney(inv.amount_paid, inv.currency ?? "BRL")}
                  </TableCell>

                  {/* Status */}
                  <TableCell className="py-2 text-xs">
                    <Badge variant="outline" className="h-5 text-[10px]">
                      {inv.status ?? "—"}
                    </Badge>
                  </TableCell>

                  {/* Link */}
                  <TableCell className="py-2 text-xs">
                    {inv.invoice_url || inv.invoice_pdf ? (
                      <a
                        href={inv.invoice_url ?? inv.invoice_pdf ?? "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-primary hover:underline"
                      >
                        abrir <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
